import { DatabaseViewAssignmentParamRequest } from '../../../api/api-types/database-view-request.api';
import { DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { buildDatabaseListRequest, DatabaseListQueryState } from '../shared/utils/database-request-builder';
import { resolveReferenceDateParam } from '../shared/utils/database-reference-date';

export type AssignmentListFilterKey =
	| 'clientName'
	| 'phase'
	| 'assignmentType'
	| 'careType'
	| 'initiatingOffice'
	| 'caseGroup'
	| 'basis'
	| 'ifd'
	| 'service'
	| 'advisor'
	| 'sponsorGroup'
	| 'sponsorCategory'
	| 'disabilityType';

export type AssignmentListSortField =
	| 'clientName'
	| 'phase'
	| 'assignmentType'
	| 'careType'
	| 'initiatingOffice'
	| 'caseGroup'
	| 'basis'
	| 'ifd'
	| 'service'
	| 'advisor'
	| 'sponsorGroup'
	| 'sponsorCategory'
	| 'disabilityType'
	| 'assignmentNo'
	| 'owner'
	| 'counselingStart'
	| 'assignmentStart'
	| 'deregistrationDate'
	| 'reportDue'
	| 'lastThreeContactDates';

export type AssignmentListQueryState = DatabaseListQueryState<AssignmentListFilterKey, AssignmentListSortField>;

const filterParamMap: Partial<Record<AssignmentListFilterKey, string>> = {
	clientName: 'client',
	phase: 'phase',
	assignmentType: 'assignment',
	careType: 'typeOfCare',
	initiatingOffice: 'initiator',
	caseGroup: 'caseGroup',
	basis: 'legalFoundation',
	ifd: 'integrationServiceOffice',
	service: 'service',
	advisor: 'advisor',
	sponsorGroup: 'assignmentSponsor',
	sponsorCategory: 'categorySponsor',
	disabilityType: 'careRelatedHandicap',
};

const sortParamMap: Partial<Record<AssignmentListSortField, string>> = {
	clientName: 'client',
	phase: 'phase',
	assignmentType: 'assignment',
	careType: 'typeOfCare',
	initiatingOffice: 'initiator',
	caseGroup: 'caseGroup',
	basis: 'legalFoundation',
	ifd: 'integrationServiceOffice',
	service: 'service',
	advisor: 'advisor',
	sponsorGroup: 'assignmentSponsor',
	sponsorCategory: 'categorySponsor',
	disabilityType: 'careRelatedHandicap',
	owner: 'advisor',
	counselingStart: 'startOfConsultation',
	assignmentStart: 'startOfCare',
	deregistrationDate: 'cancellationDate',
	assignmentNo: 'assignmentNumber',
	reportDue: 'reportDueDate',
	lastThreeContactDates: 'lastThreeContactDates',
};

export const ASSIGNMENT_DATE_RANGE_FIELD_IDS = ['startOfConsultation', 'startOfCare', 'cancellationDate'] as const;

export type AssignmentDateRangeFieldId = (typeof ASSIGNMENT_DATE_RANGE_FIELD_IDS)[number];

export const ASSIGNMENT_VISIBLE_COLUMNS = [
	'client',
	'phase',
	'assignment',
	'typeOfCare',
	'initiator',
	'caseGroup',
	'legalFoundation',
	'integrationServiceOffice',
	'service',
	'advisor',
	'assignmentSponsor',
	'categorySponsor',
	'careRelatedHandicap',
	'startOfConsultation',
	'startOfCare',
	'cancellationDate',
	'assignmentNumber',
	'reportDueDate',
	'institutionAtStart',
	'institutionAtDate',
	'institutionAtEnd',
	'schoolNumberAtStart',
	'schoolNumberAtDate',
	'schoolNumberAtEnd',
	'socialSecurityStatusAtStart',
	'socialSecurityStatusAtDate',
	'socialSecurityStatusAtEnd',
	'disabilityTypeAtStart',
	'disabilityTypeAtDate',
	'disabilityTypeAtEnd',
	'disabilityProofAtStart',
	'disabilityProofAtDate',
	'disabilityProofAtEnd',
	'gdbAtStart',
	'gdbAtDate',
	'gdbAtEnd',
	'lastThreeContactDates',
	'contactTaskCount',
	'impedimentsAtStart',
	'impedimentsAtEnd',
];

export function buildDatabaseViewAssignmentRequest(
	state: AssignmentListQueryState,
	schema?: DatabaseViewSchema | null,
	selectedColumns: string[] = ASSIGNMENT_VISIBLE_COLUMNS,
	referenceDate: Date | null = null,
): DatabaseViewAssignmentParamRequest {
	const request = buildDatabaseListRequest(
		state,
		{
			filterParamMap,
			sortParamMap,
			dateRangeFieldIds: ASSIGNMENT_DATE_RANGE_FIELD_IDS,
		},
		{ schema, selectedColumns },
	);
	const resolvedReferenceDate = resolveReferenceDateParam(referenceDate, selectedColumns);
	return resolvedReferenceDate ? { ...request, referenceDate: resolvedReferenceDate } : request;
}
